import type { ReactNode } from "react";
import { useStatsigClient } from "../../../hooks";
import { SkillsBlock } from "../../dashboard/home/components/profile/SkillsBlock";
import { SkillSummary } from "../../dashboard/home/components/profile/SkillSummary";
import { ProfileSummaryContent } from "../../dashboard/home/components/link-creator";
import ProfileEducation from "../ProfileEducation";
import ProfilePersonalInfo from "../ProfilePersonalInfo";
import ProfileProject from "../ProfileProject";
import ProfileWorkplace from "../ProfileWorkplace";
import { ProfileBrandingHeader } from "./ProfileBrandingHeader";
import type { CVTemplate, CVTemplateProps } from "./types";

/**
 * MinimalTemplate — a clean, airy, monochrome single-column layout. The second
 * CV template (step 4 of the CV-template refactor; see /CV_TEMPLATES_PLAN.md),
 * built to prove that a distinct template is just presentation + tokens over the
 * same ProfileViewModel — no data logic is duplicated.
 *
 * Differences from ClassicTemplate are purely visual: section headings are small,
 * uppercase, letter-spaced and underlined (no bullet), colored from the neutral
 * token roles (muted/line) rather than the org section-title color, with airier
 * spacing. It reuses the same content child components and preserves the same CSS
 * pagination rules as the classic PDF layout.
 */
const MinimalTemplate = ({
  vm,
  tokens,
  pdfMode,
  isEditable = false,
  logoUrl,
  showBranding,
  onEditPitch,
  onEditName,
  onEditTitle,
  onEditIntro,
  onEditHighlight,
  onEditWorkplace,
  onRemoveSkill,
  onAddStackSkill,
  onAddTopSkill,
  onRemoveTopSkill,
}: CVTemplateProps) => {
  const statsigClient = useStatsigClient();

  const sectionHeading = (title: ReactNode): ReactNode => (
    <div
      className="text-[11px] uppercase tracking-[0.18em] font-semibold pb-2 mb-4"
      style={{
        color: tokens.colors.muted,
        borderBottom: `1px solid ${tokens.colors.line}`,
        breakAfter: "avoid",
        pageBreakAfter: "avoid",
      }}
    >
      {title}
    </div>
  );

  const renderSkills = () => {
    // Layout (cloud/list) takes precedence; SkillSummary stays only as a
    // backward-compat fallback for legacy sortedMode links.
    if (!vm.skills.displayAsList && vm.skills.sortedMode !== "" && vm.skills.skillCategoryBlocks) {
      return (
        <SkillSummary
          skillCategoryBlocks={vm.skills.skillCategoryBlocks}
          displayIcons={vm.skills.displayIcons}
          pdfMode={pdfMode}
          onRemoveSkill={onRemoveSkill}
        />
      );
    }

    return (
      <SkillsBlock
        skillCategoryBlocks={vm.skills.skillCategoryBlocks}
        editable={false}
        hideIcons={!vm.skills.displayIcons}
        pdfMode={pdfMode}
        onRemoveSkill={onRemoveSkill}
        onAddSkill={onAddStackSkill}
        asList={vm.skills.displayAsList}
        order={vm.skills.skillOrder}
        skillCounts={vm.skills.skillCounts}
      />
    );
  };

  return (
    <div style={{ backgroundColor: tokens.colors.background }}>
      {showBranding && <ProfileBrandingHeader branding={vm.branding} tokens={tokens} />}
      <ProfilePersonalInfo
        name={vm.personalInfo.editedName || vm.personalInfo.name}
        title={vm.personalInfo.editedTitle || vm.personalInfo.title}
        pitch={vm.personalInfo.pitch}
        editedPitch={vm.personalInfo.editedPitch}
        profilePhotoId={vm.personalInfo.profilePhotoId}
        isEditable={false}
        useProfilePicture={vm.personalInfo.useProfilePicture}
        roundedProfilePicture={tokens.photo === "round"}
        hidePersonalInfo={vm.personalInfo.hidePersonalInfo}
        useBorder={false}
        onShowCropTool={() => {}}
        onProfileUpdate={() => {}}
        isDarkModeActive={false}
        hidePitch={vm.personalInfo.hidePitch}
        updatedPhotoId={null}
        isUploadingPhoto={false}
        removePadding={false}
        logoUrl={logoUrl}
        onEditPitch={onEditPitch}
        onEditName={onEditName}
        onEditTitle={onEditTitle}
      />

      {vm.linkConfig && vm.showProfileSummary && (
        <div className="mb-10" style={{ pageBreakInside: "avoid" }}>
          {sectionHeading(vm.sectionTitles.profile_summary)}
          <ProfileSummaryContent
            linkConfigData={vm.linkConfig}
            pdfMode={pdfMode}
            flat={true}
            onEditIntro={onEditIntro}
            onEditHighlight={onEditHighlight}
            onAddTopSkill={onAddTopSkill}
            onRemoveTopSkill={onRemoveTopSkill}
          />
        </div>
      )}

      {statsigClient?.checkGate("highlighted_projects") && vm.highlightedProjects.length > 0 && (
        <div className="mb-10">
          {sectionHeading(vm.sectionTitles.highlighted_projects)}
          <div className="flex flex-col gap-8 pl-3">
            {vm.highlightedProjects.map((highlightedProject) => (
              <div
                key={highlightedProject.title}
                style={{ breakInside: "avoid", pageBreakInside: "avoid" }}
              >
                <ProfileWorkplace
                  id={highlightedProject.title}
                  isEditable={false}
                  name={
                    highlightedProject.workplaceId
                      ? vm.getWorkplaceName(highlightedProject.workplaceId ?? "")
                      : highlightedProject.projectId
                        ? vm.getWorkplaceNameFromProject(highlightedProject.projectId)
                        : ""
                  }
                  skillTags={highlightedProject.skillTags ?? []}
                  role={highlightedProject.title ?? ""}
                  description={highlightedProject.summary}
                  isProject={true}
                  showSkills={vm.linkConfig?.highlightedProjectsConfig?.showSkills ?? false}
                  showSkillIcons={vm.linkConfig?.highlightedProjectsConfig?.showIcons ?? false}
                  showDescription={true}
                  onWorkplaceEdit={(_description: string) => {}}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {vm.skills.displaySkillsSummary &&
        vm.skills.placeInTop === true &&
        vm.skills.skillTagsExist && (
          <div className="mb-10" style={{ pageBreakInside: "avoid" }}>
            {sectionHeading(vm.sectionTitles.skill_stack)}
            <div className="pl-3">{renderSkills()}</div>
          </div>
        )}

      {vm.linkConfig?.profileLinkWorkplaceIds &&
        vm.linkConfig.profileLinkWorkplaceIds.length > 0 && (
          <div className="mb-10 pl-3 bg-white">
            {vm.workplaces.map((workplace, index) => (
              <div
                className="mb-8"
                key={workplace.id}
                style={{
                  backgroundColor: "#fff",
                  ...(vm.avoidWorkplacePageBreak && vm.avoidWorkplaceDescriptionPageBreak
                    ? { breakInside: "avoid", pageBreakInside: "avoid" }
                    : {}),
                }}
              >
                {index === 0 && sectionHeading(vm.sectionTitles.work_experience)}
                <ProfileWorkplace
                  id={workplace.id}
                  isEditable={isEditable}
                  fieldsEditable={!!onEditWorkplace}
                  name={workplace.name}
                  skillTags={workplace.skillTags}
                  role={workplace.role}
                  pdfMode={pdfMode}
                  description={workplace.description}
                  startDate={workplace.startDate}
                  endDate={workplace.endDate}
                  showSkills={vm.linkConfig?.workplaceLinkConfig?.displaySkills}
                  showSkillIcons={vm.linkConfig?.workplaceLinkConfig?.displaySkillIcons ?? true}
                  showDescription={vm.linkConfig?.workplaceLinkConfig?.displayDescription}
                  avoidPageBreak={vm.avoidWorkplacePageBreak}
                  onWorkplaceEdit={(description) =>
                    onEditWorkplace?.(workplace.id, { description })
                  }
                  onWorkplaceContentUpdate={(_id, role, _description, name) =>
                    onEditWorkplace?.(workplace.id, { role, name })
                  }
                  onProjectClickAction={() => {}}
                />
              </div>
            ))}
          </div>
        )}

      {vm.linkConfig?.profileLinkProjects && vm.linkConfig.profileLinkProjects.length > 0 && (
        <div className="mb-10 pl-3">
          {vm.projects.map((project, index) => (
            <div className="mb-8" key={project.id} style={{ backgroundColor: "#fff" }}>
              {index === 0 && sectionHeading(vm.sectionTitles.projects)}
              <ProfileProject
                id={project.id}
                isEditable={false}
                name={
                  project.isSideProject
                    ? "Side project"
                    : vm.getWorkplaceName(project.workplaceId ?? "")
                }
                skillTags={project.skillTags}
                title={project.title}
                description={project.pitch}
                showSkills={vm.linkConfig?.projectsLinkConfig?.displaySkills}
                showDescription={true}
                showSkillIcons={vm.linkConfig?.projectsLinkConfig?.displaySkillIcons ?? true}
                onWorkplaceEdit={() => {}}
                onWorkplaceContentUpdate={() => {}}
                onProjectClickAction={() => {}}
                onWorkplaceNameClick={() => {}}
                profileId={vm.accountProfileId}
                pdfMode={pdfMode}
              />
            </div>
          ))}
        </div>
      )}

      {vm.showEducationSection && vm.educations.length > 0 && (
        <div className="mb-10">
          {sectionHeading(vm.sectionTitles.education)}
          <div className="pl-3">
            {vm.educations.map((education, index) => (
              <div className="mb-6" key={education.id}>
                <ProfileEducation
                  isEditable={false}
                  isDarkModeActive={false}
                  key={index}
                  isBeingEdited={false}
                  isNew={false}
                  education={education}
                  onSaveItem={() => {}}
                  onCancelEdit={() => {}}
                  onRemove={() => {}}
                  onShowDialog={() => {}}
                  isLastInOrder={false}
                  onContentImprovementStarted={() => {}}
                  onContentImprovementEnded={() => {}}
                  pdfMode={false}
                  removePadding={true}
                  staticMode={true}
                  onProjectClickAction={() => {}}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {vm.skills.placeInTop === false && vm.skills.skillTagsExist && (
        <div className="mb-10" style={{ pageBreakInside: "avoid" }}>
          {sectionHeading(vm.sectionTitles.skill_stack)}
          <div className="pl-3">{renderSkills()}</div>
        </div>
      )}
    </div>
  );
};

export const minimalTemplate: CVTemplate = {
  id: "minimal",
  name: "Minimal",
  Component: MinimalTemplate,
};

export default MinimalTemplate;
